/**
 * serverSync — pusht elke echte store-wijziging (gedempt) naar de server.
 *
 * De store (localStorage) blijft de bron van waarheid in Beheer; deze module
 * spiegelt hem naar de server-data-store, zodat /api/rooster en /api/mijn-rooster
 * dezelfde data zien. De payload is het .data-deel van bouwBackupBestand — exact
 * de tien gepersisteerde velden — zodat er geen tweede definitie van "de data"
 * ontstaat.
 *
 * Activering: één keer importeren in de app-entry (main.jsx). De subscribe hieronder
 * draait dan voor de hele levensduur van de pagina.
 */
import { useRoosterStore, resetRooster } from '../store/useRoosterStore.js';
import { bouwBackupBestand } from './backup.js';
import { stuurData, haalData, haalWijzigingenStil, WIJZIGINGEN_OPGESLAGEN_EVENT } from './api.js';
import { zetActieveOmgeving } from './omgeving.js';

const DEBOUNCE_MS = 600;

let timer = null;
let laatstVerstuurd = null; // JSON-string van de laatst verstuurde payload

// Push-grendel: deze sessie mag pas naar de server schrijven nadat we de server ÉÉN keer
// als bron hebben geladen. Zonder grendel duwt de eerste de beste store-wijziging de
// (mogelijk verouderde) localStorage over de live data heen — zo verdwenen een goedgekeurde
// ruil en de vastgelegde weken van een hele maand. Niet kunnen pushen is de veilige kant:
// er gaat niets van de server verloren, hooguit landt een lokale bewerking (nog) niet.
let serverGeladen = false;

// Het data-deel (de tien velden) uit de huidige store-state.
function huidigeData() {
  return bouwBackupBestand(useRoosterStore.getState()).data;
}

// Verstuur nu — maar alleen als de data echt gewijzigd is t.o.v. de vorige push.
function syncNu() {
  if (!serverGeladen) return; // grendel: nooit blind localStorage over de server heen duwen
  const data = huidigeData();
  const payload = JSON.stringify(data);
  if (payload === laatstVerstuurd) return; // niets veranderd -> niets sturen
  laatstVerstuurd = payload;
  stuurData(data).catch(() => {
    // Stil opvangen (server offline/fout mag de UI niet breken). laatstVerstuurd
    // terugzetten zodat een volgende wijziging het opnieuw probeert.
    laatstVerstuurd = null;
  });
}

// Plan een gedempte sync: snel achter elkaar wijzigen -> één push.
function planSync() {
  if (timer) clearTimeout(timer);
  timer = setTimeout(syncNu, DEBOUNCE_MS);
}

// Elke state-wijziging plant een sync.
useRoosterStore.subscribe(planSync);

// ── laadkant: bij het opstarten de server als bron nemen ─────────────────────

// Lichte validatie: alleen een plausibel data-object (met regels.medewerkers) vertrouwen.
function geldig(data) {
  return !!(data && typeof data === 'object' && data.regels && typeof data.regels === 'object' && data.regels.medewerkers && typeof data.regels.medewerkers === 'object');
}

// Haal de server-data en vul daarmee de store. De baseline (laatstVerstuurd) wordt NA
// setState gezet, in de PUT-vorm — zie de toelichting bij de baseline hieronder.
let laadtBezig = false;
export async function laadVanServer() {
  if (laadtBezig) return;
  laadtBezig = true;
  try {
    const data = await haalData();
    if (!geldig(data)) return;                 // ongeldig -> localStorage behouden, grendel blijft dicht
    // Welke lade de server teruggaf (stap 6a: envelope.omgeving). Vastleggen VÓÓR setState:
    // de store-persist schrijft naar de bucket van de ACTIEVE omgeving, dus die moet eerst
    // kloppen — anders landt deze data in de bucket van de vorige omgeving. Ontbreekt het veld
    // (oude server), dan is zetActieveOmgeving een no-op en blijft de bestaande keuze staan.
    zetActieveOmgeving(data.omgeving);
    // ...resetRooster: setState is een MERGE, dus zonder deze reset blijft het runtime-veld
    // maandrooster (enkelvoud, niet gepersisteerd) op de vorige generatie staan terwijl
    // maandroosters en vastgelegdeWeken eronder net vervangen zijn. De matrix toont dan
    // verouderde data zonder enig signaal — erger dan een lege matrix, want het valt niet op.
    // Exact dezelfde reset die importBackup toepast (useRoosterStore.js).
    useRoosterStore.setState({ ...data, ...resetRooster });
    // Baseline NA setState, en via huidigeData() zodat hij DEZELFDE vorm heeft als wat
    // syncNu() straks berekent. Hij stond hier eerst vóór setState op JSON.stringify(data),
    // maar dat is de GET-vorm (15 velden, met `omgeving`) terwijl syncNu de PUT-vorm bouwt
    // (BACKUP_VELDEN + `actieveOmgeving`). Die twee strings zijn per definitie ongelijk, dus
    // de guard in syncNu sloeg nooit aan en de eerstvolgende store-wijziging pushte altijd —
    // alleen inloggen was al genoeg. NA setState mag: subscribe → planSync zet enkel een
    // timer van DEBOUNCE_MS, dus syncNu kan niet tussen deze twee synchrone regels door
    // draaien. En het moet NA zetActieveOmgeving, want huidigeData() leest die waarde mee.
    laatstVerstuurd = JSON.stringify(huidigeData());
    serverGeladen = true;                      // pas nu is de store server-vers: pushen mag
  } catch {
    // server onbereikbaar / geen toegang (pre-login 403) -> stil, localStorage blijft de bron
  } finally {
    laadtBezig = false;
  }
}

// ── smalle verversing: ALLEEN het veld `wijzigingen` ─────────────────────────
/**
 * Haalt het wijzigingslogboek opnieuw op en zet UITSLUITEND dat ene veld in de store.
 *
 * ⚠️ NADRUKKELIJK NIET via laadVanServer(). Die doet setState({ ...data, ...resetRooster })
 * (r.84) en vervangt daarmee maandroosters én vastgelegdeWeken door de serverstand, terwijl
 * `maandrooster` op null gaat. Midden in het bewerken van de matrix zou dat het scherm
 * leeggooien en lokaal werk dat nog niet gepusht is overschrijven. Deze functie raakt één
 * veld aan en laat de rest met rust.
 *
 * De poort is `serverGeladen`: die staat alleen op true nadat laadVanServer één keer geslaagd
 * is, en dat gebeurt uitsluitend voor een ingelogde teamleider (useRol.js:99). Een medewerker
 * komt hier dus nooit voorbij — en mocht de call tóch gedaan worden, dan is haalWijzigingenStil
 * een kale fetch die bij 401/403 gewoon null teruggeeft zonder de sessie-overlay te triggeren.
 *
 * Faalt stil: null → de bestaande stand blijft staan. Een teken dat één bewerking later
 * verschijnt is beter dan een foutmelding over iets waar niemand om gevraagd heeft.
 *
 * Deze setState wekt wél de subscribe hierboven (r.56) en plant dus een sync. Die loopt
 * meteen dood: syncNu bouwt de PUT-vorm uit BACKUP_VELDEN (backup.js:20-31) en `wijzigingen`
 * staat daar niet in, dus de payload is ongewijzigd en de guard op r.40 stopt hem. Er gaat
 * hierdoor dus geen enkele PUT extra de deur uit.
 */
let verversBezig = false;
async function ververWijzigingen() {
  if (!serverGeladen || verversBezig) return;
  verversBezig = true;
  try {
    const wijzigingen = await haalWijzigingenStil();
    if (Array.isArray(wijzigingen)) useRoosterStore.setState({ wijzigingen });
  } finally {
    verversBezig = false;
  }
}

// wijzigingSync geeft dit event af na een geslaagde POST met cel-records. Module-niveau, net
// als de subscribe hierboven: serverSync wordt één keer geïmporteerd in main.jsx en leeft dan
// zolang de pagina leeft.
if (typeof window !== 'undefined') {
  window.addEventListener(WIJZIGINGEN_OPGESLAGEN_EVENT, () => { ververWijzigingen(); });
}

// GEEN automatische load bij module-load: dat draait vóór de login (geen token -> 401).
// useRol roept laadVanServer() aan zodra een teamleider is ingelogd (dan is er een geldig token).
